<template>
  <form :class="{ 'full-height': !isCheckout }" class="flex flex-col justify-between" @submit.prevent="checkForm">
    <div>
      <p class="font-bold mb-5">
        {{ isCheckout ? 'Confirm your contact details' : 'We need a bit of information about you.' }}
      </p>
      <div>
        <div class="sm:flex">
          <DrInput
            v-model="firstName"
            :error="errors.firstName"
            data-label="First name"
            class="mb-6 sm:mr-1"
            placeholder="First name"
            icon="user"
            name="fname"
            :disabled="loading"
            autocomplete="given-name"
          />
          <DrInput
            v-model="lastName"
            :error="errors.lastName"
            data-label="Last name"
            class="mb-6"
            placeholder="Last name"
            icon="user"
            name="lname"
            :disabled="loading"
            autocomplete="family-name"
          />
        </div>
        <DrInput
          v-model="email"
          :error="errors.email"
          :class="{ 'mb-6': isCheckout }"
          data-label="Email address"
          class="mb-1"
          type="email"
          placeholder="Email address"
          icon="email"
          name="email"
          :disabled="loading"
          autocomplete="email"
        />

        <transition name="fade">
          <div v-if="emailThirdPartyCheckFailed" :class="{ '-mt-3 mb-3': isCheckout }">
            <DrInput
              v-model="emailConfirmation"
              :error="errors.emailConfirmation"
              data-label="Confirm email address"
              class="mb-1"
              type="email"
              placeholder="Email address"
              icon="email"
              name="email-confirm"
              :disabled="loading"
            />
            <p
              v-if="emailConfirmation === ''" class="px-1 text-red-650 text-xs mb-0"
            >
              Your email may not be right, please confirm it's correct.
            </p>
          </div>
        </transition>

        <p
          v-if="!isCheckout" class="px-1 text-2xs"
        >
          We use your email address to save your personalised quote, so you can come back to it at anytime.
        </p>

        <DrInput
          v-if="isCheckout"
          v-model="phone"
          :error="errors.phone"
          data-label="Phone number"
          type="tel"
          class="mb-6"
          placeholder="07712345678"
          name="phone"
          :disabled="loading"
          autocomplete="tel"
        />
      </div>
    </div>
    <div>
      <DrButton
        :loading="loading"
        :class="{ 'btn-cyan': !isCheckout && !reference }"
        data-cy="btn-start-cover"
        class="btn-block btn-blue"
        @click.prevent="checkForm"
      >
        {{ isCheckout ? 'Continue' : reference ? 'Continue' : 'Start finding cover' }}
      </DrButton>
      <p v-if="!isCheckout" class="text-2xs mt-4 mb-0 text-center leading-tight text-grey-550">
        By using this service you are agreeing to
        <br>our
        <a class="no-underline text-blue-500" href="https://docs.digitalrisks.co.uk/terms_of_business_agreement.pdf" rel="noopener noreferrer">terms of business</a> and
        <a class="no-underline text-blue-500" href="https://www.digitalrisks.co.uk/privacy-notice" target="_blank" rel="noopener noreferrer">privacy notice.</a>
      </p>
    </div>
  </form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import _isEmpty from 'lodash.isempty'
import Cookies from 'js-cookie'
import axiosQuoteApi from '@/services/axiosQuoteApi'
import axiosEmailChecker from '@/services/axiosEmailChecker'
import getSlidesMixin from '@/mixins/getSlides'

export default {
  mixins: [getSlidesMixin],
  data () {
    return {
      loading: false,
      errors: {},
      emailThirdPartyCheckFailed: false,
      emailConfirmation: '',
    }
  },
  computed: {
    ...mapGetters({
      getChargebeeEstimate: 'getChargebeeEstimate',
      couponIds: 'couponIds',
      getSource: 'getSource',
    }),
    isCheckout () {
      return this.$route.path === '/checkout/personal'
    },
    firstName: {
      get () {
        return this.$store.getters.getFirstName
      },
      set (value) {
        value = value.charAt(0).toUpperCase() + value.slice(1)
        this.$store.commit('updateFirstName', value)
      },
    },
    lastName: {
      get () {
        return this.$store.getters.getLastName
      },
      set (value) {
        value = value.charAt(0).toUpperCase() + value.slice(1)
        this.$store.commit('updateLastName', value)
      },
    },
    email: {
      get () {
        return this.$store.getters.getEmail
      },
      set (value) {
        this.$store.commit('updateEmail', value)
      },
    },
    phone: {
      get () {
        return this.$store.getters.getPhone
      },
      set (value) {
        this.$store.commit('updatePhone', value)
      },
    },
    reference: {
      get () {
        return this.$store.getters.getReference
      },
      set (value) {
        this.$store.commit('updateReference', value)
      },
    },
    businessName: {
      get () {
        return this.$store.getters.getBusinessName
      },
      set (value) {
        this.$store.commit('updateBusinessName', value)
      },
    },
  },
  methods: {
    ...mapActions({
      storePremises: 'premises/storePremises',
    }),
    async checkForm () {
      this.loading = true
      this.errors = {}

      if (!this.firstName) this.errors.firstName = 'First name is required'
      if (!this.lastName) this.errors.lastName = 'Last name is required'
      if (!this.email) this.errors.email = 'Email address is required'
      if (this.email && !this.validEmail(this.email)) this.errors.email = 'Email address is not valid'
      if (this.isCheckout && !this.phone) this.errors.phone = 'Phone number is required'

      if (!_isEmpty(this.errors)) {
        this.loading = false
        return
      }

      if (this.isCheckout) {
        const formattedPhone = this.phone.replace(/\s/g, '')
        const phoneRegEx = /^[0-9)(+]+$/

        if (!phoneRegEx.test(formattedPhone) || formattedPhone.length < 9 || formattedPhone.length > 20) {
          this.loading = false
          return (this.errors.phone = 'Phone number is not valid')
        }
      }

      // Email verification - should only run once in the quote process
      if (!this.$store.state.emailCheck.verified) {
        if (!this.emailThirdPartyCheckFailed && !(await this.emailCheckByThirdParty())) {
          this.emailThirdPartyCheckFailed = true
          this.loading = false
          return
        }
      }

      // Manual email verification if third party failed
      if (!this.$store.state.emailCheck.verified) {
        if (this.emailVerifiedByUserConfirmation() === false) {
          this.errors.emailConfirmation = 'Email does not match'
          this.errors.email = 'Email does not match'
          this.loading = false
          return
        }
      }

      return this.submitForm()
    },
    validEmail (email) {
      const regExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line no-useless-escape
      return regExp.test(email)
    },
    async emailCheckByThirdParty () {
      let isEmailDeliverable = false
      try {
        const emailCheckResponse = await axiosEmailChecker.get(`/v3/lookups/json?email=${this.email}`)
        isEmailDeliverable = !!emailCheckResponse.data.deliverable
      } catch (e) {}

      if (isEmailDeliverable) this.$store.commit('emailCheck/verify')

      return isEmailDeliverable
    },
    emailVerifiedByUserConfirmation () {
      if (this.email !== this.emailConfirmation) return false

      // Email verfied by user confirmation
      this.$store.commit('emailCheck/verify')
      return true
    },
    submitForm () {
      if (
        (!this.$store.state.contact.businessNumber && !this.$store.state.contact.isValidBusiness) ||
        (!this.isCheckout && !this.businessName)
      ) { this.businessName = `${this.firstName} ${this.lastName}` }

      const contactData = this.$store.state.contact

      if (this.$store.getters.shouldUrbanConsent) {
        contactData.urbanConsent = this.$store.state.urbanConsent
      }

      if (this.isCheckout) {
        if (this.getChargebeeEstimate.total) {
          contactData.quoteTotalValue = this.getChargebeeEstimate.total / 100
        } else {
          contactData.quoteTotalValue = this.$store.getters.getTotal
        }
      }

      if (!this.isCheckout) {
        const reference = this.$store.getters.getReference
        axiosQuoteApi[reference ? 'put' : 'post'](`quote/${this.$store.state.quoteId}`, {
          contact: contactData,
        })
          .then((response) => {
            this.loading = false

            if (response.data) {
              this.reference = response.data.reference

              if (this.couponIds && this.couponIds.length && response.data.reference) {
                axiosQuoteApi.post(`quote/${this.$store.state.quoteId}/apply-coupon`, {
                  couponId: this.couponIds,
                })
              }
            }
            // save quote id to cookies
            if (!this.$store.state.portal) {
              Cookies.set('dr-store', JSON.stringify({ quoteId: this.$store.getters.getQuoteId }), { expires: 2 })
              this.$store.dispatch('initialiseStore').then(() => {
                // @TODO - for v2 this would be better in a custom journey or the router
                if (this.getSource === 'appear-here') {
                  this.getSlides('CommercialCombined\\AppearHere')
                  return
                }
                reference ? this.$router.push('/products') : this.$router.push('/products/start')
              })
            }
          })
          .catch(() => {
            this.loading = false
            this.$router.push('/404')
          })
      } else {
        axiosQuoteApi
          .put(`quote/${this.$store.state.quoteId}/contact/extended`, contactData)
          .then((response) => {
            this.loading = false

            this.$router.push('/checkout/policyholder')
          })
          .catch(() => {
            this.loading = false
            this.$router.push('/404')
          })
      }
    },
  },
}
</script>
